import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { FileText, ArrowRight, Plus, Search, Loader2, X, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';
import { extractTemplateContent } from '../utils/templateUtils';
import { useTranslation } from '../lib/i18n';

interface Template {
  id: string;
  name: string;
  description: string;
  category: string;
  content: { text: string };
}

interface Category {
  id: string;
  name: string;
  count: number;
}

interface TemplateRequest {
  name: string;
  description: string;
  category: string;
}

const Templates = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<Template[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [creatingDocument, setCreatingDocument] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [templateRequest, setTemplateRequest] = useState<TemplateRequest>({
    name: '',
    description: '',
    category: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      console.log('👤 User found, loading templates...');
      loadTemplates();
    } else {
      console.log('❌ No user found');
    }
  }, [user]);

  useEffect(() => {
    // Calculate categories after templates are loaded
    if (templates.length > 0) {
      const categoryMap = new Map<string, number>();
      templates.forEach((template) => {
        const category = template.category || 'uncategorized';
        const count = categoryMap.get(category) || 0;
        categoryMap.set(category, count + 1);
      });

      const categoriesArray = Array.from(categoryMap.entries()).map(([id, count]) => ({
        id,
        name: id ? id.charAt(0).toUpperCase() + id.slice(1) : t('templates.uncategorized'),
        count,
      }));

      setCategories(categoriesArray.sort((a, b) => a.name.localeCompare(b.name)));
    }
  }, [templates]);
  const loadTemplates = async () => {
    try {
      setLoading(true);
      console.log('📚 Loading templates from database...');
      
      // Load templates from database
      const { data: dbTemplates, error: dbError } = await supabase
        .from('templates')
        .select('*')
        .order('name');

      if (dbError) {
        console.error('❌ Error loading templates from database:', dbError);
        // Set empty array but don't throw error - this is expected if no templates exist
        setTemplates([]);
      } else {
        console.log('✅ Templates loaded from database:', dbTemplates?.length || 0);
        console.log('📄 First template sample:', dbTemplates?.[0]);
        setTemplates(dbTemplates || []);
      }

    } catch (error) {
      console.error('❌ Error loading templates:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setSubmitting(true);
    setRequestError('');
    setRequestSuccess(false);

    try {
      // Get fresh session to ensure user ID matches RLS policy
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session?.user) {
        throw new Error(t('templates.authRequired'));
      }

      const { error } = await supabase
        .from('template_requests')
        .insert([
          {
            name: templateRequest.name,
            description: templateRequest.description,
            category: templateRequest.category,
            requested_by: session.user.id,
            status: 'pending'
          }
        ]);

      if (error) throw error;

      setRequestSuccess(true);
      setTemplateRequest({
        name: '',
        description: '',
        category: ''
      });

      // Close modal after 2 seconds
      setTimeout(() => {
        setShowRequestModal(false);
        setRequestSuccess(false);
      }, 2000);
    } catch (error: any) {
      console.error('Error submitting template request:', error);
      setRequestError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const createDocumentFromTemplate = async (template: Template) => {
    try {
      setCreatingDocument(true);
      setError(null);

      const documentTitle = template.name;
      let templateContent = extractTemplateContent(template.content);
      
      if (!templateContent) {
        console.warn('⚠️ No content found in template, using empty document');
        templateContent = '';
      }
      
      // Navigate to editor with template content as state
      navigate('/editor', {
        state: {
          templateContent,
          templateTitle: documentTitle
        }
      });
    } catch (error) {
      console.error('❌ Error loading template:', error);
      setError(error.message || t('templates.failedLoadTemplate'));
    } finally {
      setCreatingDocument(false);
    }
  };

  const filteredTemplates = templates.filter(
    (template) =>
      (!selectedCategory || (template.category || 'uncategorized') === selectedCategory) &&
      (template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  if (!user) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-bold mb-4">{t('templates.pleaseSignIn')}</h2>
        <p className="mb-8">{t('templates.pleaseSignInMessage')}</p>
        <Link to="/auth">
          <Button>{t('common.signIn')}</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">{t('templates.title')}</h1>
        <Button variant="outline" onClick={() => setShowRequestModal(true)}>
          {t('templates.requestTemplate')}
        </Button>
      </div>

      {/* Template Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold">{t('templates.requestNewTitle')}</h2>
              <button
                onClick={() => setShowRequestModal(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {requestError && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-lg">
                {requestError}
              </div>
            )}

            {requestSuccess && (
              <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-lg">
                {t('templates.requestSuccess')}
              </div>
            )}

            <form onSubmit={handleRequestSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('templates.templateName')}</label>
                <input
                  type="text"
                  value={templateRequest.name}
                  onChange={(e) => setTemplateRequest(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white font-semibold"
                  required
                  disabled={submitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('templates.category')}</label>
                <input
                  type="text"
                  value={templateRequest.category}
                  onChange={(e) => setTemplateRequest(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white font-semibold"
                  required
                  disabled={submitting}
                  placeholder={t('templates.categoryPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('templates.description')}</label>
                <textarea
                  value={templateRequest.description}
                  onChange={(e) => setTemplateRequest(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 h-32 dark:text-white font-semibold"
                  required
                  disabled={submitting}
                  placeholder={t('templates.descriptionPlaceholder')}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('templates.submitting')}
                  </>
                ) : (
                  t('templates.submitRequest')
                )}
              </Button>
            </form>
          </div>
        </div>
      )}

      <div className="flex items-center space-x-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t('templates.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white font-semibold"
          />
        </div>
        <Button
          variant={selectedCategory ? 'default' : 'ghost'}
          onClick={() => setSelectedCategory(null)}
        >
          {t('templates.allCategories')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {categories.map((category) => (
          <div
            key={category.id}
            className={`bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md cursor-pointer transition-all duration-200 ${
              selectedCategory === category.id ? 'ring-2 ring-blue-500' : ''
            }`}
            onClick={() => setSelectedCategory(
              selectedCategory === category.id ? null : category.id
            )}
          >
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{category.name}</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4 font-semibold">
              {category.count !== 1
                ? t('templates.templatesCountPlural', { count: category.count })
                : t('templates.templatesCount', { count: category.count })}
            </p>
            <Button variant="ghost" className="w-full">
              {selectedCategory === category.id ? t('templates.showAll') : t('templates.viewCategory')}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-600 dark:text-gray-300 font-semibold">{t('templates.loading')}</p>
        </div>
      ) : filteredTemplates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map((template) => (
            <div
              key={template.id}
              className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <FileText className="w-8 h-8 text-blue-500" />
                <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                  {template.category || t('templates.uncategorized')}
                </span>
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{template.name}</h3>
              <p className="text-gray-700 dark:text-gray-200 mb-4 text-sm font-medium">
                {template.description}
              </p>
              <Button
                onClick={() => createDocumentFromTemplate(template)}
                disabled={creatingDocument}
                className="w-full"
              >
                {creatingDocument ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('templates.creating')}
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    {t('templates.useTemplate')}
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{t('templates.noTemplatesFound')}</h3>
          <p className="text-gray-600 dark:text-gray-300 mb-6 font-medium">
            {searchQuery || selectedCategory
              ? t('templates.tryAdjusting')
              : t('templates.notAvailable')}
          </p>
          {(searchQuery || selectedCategory) && (
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory(null);
              }}
            >
              {t('templates.clearFilters')}
            </Button>
          )}
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}
    </div>
  );
};

export default Templates;